// Weather forecast for the itinerary day(s).
// Uses Open-Meteo (free, no API key, no auth) — https://open-meteo.com
import { useEffect, useState } from "react";
import {
  Cloud,
  CloudRain,
  CloudDrizzle,
  CloudSnow,
  CloudFog,
  Sun,
  CloudSun,
  CloudLightning,
  CloudHail,
  Loader2,
  Wind,
  Thermometer,
  Sun as SunIcon,
} from "lucide-react";
import type { LangCode } from "@/lib/i18n";

type DayForecast = {
  date: string;
  tMax: number;
  tMin: number;
  feelsMax: number;
  feelsMin: number;
  precipMm: number;
  precipProb: number;
  windMax: number;
  uvMax: number;
  code: number;
};

const L: Record<string, Record<LangCode, string>> = {
  today: { pt: "Hoje", en: "Today", es: "Hoy", fr: "Aujourd'hui", de: "Heute", ja: "今日", it: "Oggi" },
  weather: { pt: "Tempo", en: "Weather", es: "Clima", fr: "Météo", de: "Wetter", ja: "天気", it: "Meteo" },
  rain: { pt: "chuva", en: "rain", es: "lluvia", fr: "pluie", de: "Regen", ja: "雨", it: "pioggia" },
  feels: { pt: "Sensação", en: "Feels like", es: "Sensación", fr: "Ressenti", de: "Gefühlt", ja: "体感", it: "Percepita" },
  wind: { pt: "Vento", en: "Wind", es: "Viento", fr: "Vent", de: "Wind", ja: "風", it: "Vento" },
  uv: { pt: "UV", en: "UV", es: "UV", fr: "UV", de: "UV", ja: "UV", it: "UV" },
};

// Precise Open-Meteo WMO code → icon + short label
function describeCode(code: number, lang: LangCode): { Icon: typeof Sun; label: string; tone: string } {
  const T: Record<string, Record<LangCode, string>> = {
    clear: { pt: "Limpo", en: "Clear", es: "Despejado", fr: "Dégagé", de: "Klar", ja: "晴れ", it: "Sereno" },
    mostly: { pt: "Pouco nublado", en: "Mostly clear", es: "Poco nublado", fr: "Peu nuageux", de: "Heiter", ja: "ほぼ晴れ", it: "Quasi sereno" },
    partly: { pt: "Parc. nublado", en: "Partly cloudy", es: "Parc. nublado", fr: "Partiellement nuageux", de: "Teilw. bewölkt", ja: "所により曇り", it: "Parz. nuvoloso" },
    cloudy: { pt: "Nublado", en: "Overcast", es: "Nublado", fr: "Couvert", de: "Bewölkt", ja: "曇り", it: "Nuvoloso" },
    fog: { pt: "Nevoeiro", en: "Fog", es: "Niebla", fr: "Brouillard", de: "Nebel", ja: "霧", it: "Nebbia" },
    rfog: { pt: "Nev. gelado", en: "Rime fog", es: "Niebla helada", fr: "Brouillard givrant", de: "Reifnebel", ja: "着氷性の霧", it: "Nebbia gelida" },
    drizzleL: { pt: "Chuvisco", en: "Light drizzle", es: "Llovizna ligera", fr: "Bruine légère", de: "Leichter Nieselregen", ja: "小雨", it: "Pioviggine leggera" },
    drizzle: { pt: "Chuvisco", en: "Drizzle", es: "Llovizna", fr: "Bruine", de: "Nieselregen", ja: "霧雨", it: "Pioviggine" },
    drizzleH: { pt: "Chuvisco forte", en: "Heavy drizzle", es: "Llovizna intensa", fr: "Bruine forte", de: "Starker Nieselregen", ja: "強い霧雨", it: "Pioviggine forte" },
    fdrizzle: { pt: "Chuvisco gelado", en: "Freezing drizzle", es: "Llovizna helada", fr: "Bruine verglaçante", de: "Gefr. Nieselregen", ja: "着氷性の霧雨", it: "Pioviggine gelata" },
    rainL: { pt: "Chuva fraca", en: "Light rain", es: "Lluvia ligera", fr: "Pluie légère", de: "Leichter Regen", ja: "小雨（雨）", it: "Pioggia leggera" },
    rain: { pt: "Chuva", en: "Rain", es: "Lluvia", fr: "Pluie", de: "Regen", ja: "雨", it: "Pioggia" },
    rainH: { pt: "Chuva forte", en: "Heavy rain", es: "Lluvia intensa", fr: "Pluie forte", de: "Starker Regen", ja: "強い雨", it: "Pioggia forte" },
    frain: { pt: "Chuva gelada", en: "Freezing rain", es: "Lluvia helada", fr: "Pluie verglaçante", de: "Gefr. Regen", ja: "着氷性の雨", it: "Pioggia gelata" },
    snowL: { pt: "Neve fraca", en: "Light snow", es: "Nieve ligera", fr: "Neige légère", de: "Leichter Schneefall", ja: "小雪", it: "Neve leggera" },
    snow: { pt: "Neve", en: "Snow", es: "Nieve", fr: "Neige", de: "Schnee", ja: "雪", it: "Neve" },
    snowH: { pt: "Neve forte", en: "Heavy snow", es: "Nieve intensa", fr: "Neige forte", de: "Starker Schneefall", ja: "強い雪", it: "Neve forte" },
    grains: { pt: "Grãos de neve", en: "Snow grains", es: "Granos de nieve", fr: "Grains de neige", de: "Schneegriesel", ja: "霧雪", it: "Granuli di neve" },
    showerL: { pt: "Aguaceiros fracos", en: "Light showers", es: "Chubascos ligeros", fr: "Averses légères", de: "Leichte Schauer", ja: "にわか雨（弱）", it: "Rovesci leggeri" },
    shower: { pt: "Aguaceiros", en: "Showers", es: "Chubascos", fr: "Averses", de: "Schauer", ja: "にわか雨", it: "Rovesci" },
    showerH: { pt: "Aguaceiros fortes", en: "Heavy showers", es: "Chubascos fuertes", fr: "Averses fortes", de: "Starke Schauer", ja: "激しいにわか雨", it: "Rovesci forti" },
    storm: { pt: "Trovoada", en: "Thunderstorm", es: "Tormenta", fr: "Orage", de: "Gewitter", ja: "雷雨", it: "Temporale" },
    stormH: { pt: "Trovoada c/ granizo", en: "Storm with hail", es: "Tormenta con granizo", fr: "Orage avec grêle", de: "Gewitter mit Hagel", ja: "雹を伴う雷雨", it: "Temporale con grandine" },
  };
  const sun = "text-amber-500";
  const sky = "text-sky-500";
  const slate = "text-slate-400";
  const ice = "text-cyan-400";
  const purple = "text-purple-500";
  switch (code) {
    case 0: return { Icon: Sun, label: T.clear[lang], tone: sun };
    case 1: return { Icon: CloudSun, label: T.mostly[lang], tone: sun };
    case 2: return { Icon: CloudSun, label: T.partly[lang], tone: sky };
    case 3: return { Icon: Cloud, label: T.cloudy[lang], tone: slate };
    case 45: return { Icon: CloudFog, label: T.fog[lang], tone: slate };
    case 48: return { Icon: CloudFog, label: T.rfog[lang], tone: ice };
    case 51: return { Icon: CloudDrizzle, label: T.drizzleL[lang], tone: sky };
    case 53: return { Icon: CloudDrizzle, label: T.drizzle[lang], tone: sky };
    case 55: return { Icon: CloudDrizzle, label: T.drizzleH[lang], tone: sky };
    case 56: case 57: return { Icon: CloudDrizzle, label: T.fdrizzle[lang], tone: ice };
    case 61: return { Icon: CloudRain, label: T.rainL[lang], tone: sky };
    case 63: return { Icon: CloudRain, label: T.rain[lang], tone: sky };
    case 65: return { Icon: CloudRain, label: T.rainH[lang], tone: sky };
    case 66: case 67: return { Icon: CloudRain, label: T.frain[lang], tone: ice };
    case 71: return { Icon: CloudSnow, label: T.snowL[lang], tone: ice };
    case 73: return { Icon: CloudSnow, label: T.snow[lang], tone: ice };
    case 75: return { Icon: CloudSnow, label: T.snowH[lang], tone: ice };
    case 77: return { Icon: CloudSnow, label: T.grains[lang], tone: ice };
    case 80: return { Icon: CloudRain, label: T.showerL[lang], tone: sky };
    case 81: return { Icon: CloudRain, label: T.shower[lang], tone: sky };
    case 82: return { Icon: CloudRain, label: T.showerH[lang], tone: sky };
    case 85: case 86: return { Icon: CloudSnow, label: T.snow[lang], tone: ice };
    case 95: return { Icon: CloudLightning, label: T.storm[lang], tone: purple };
    case 96: case 99: return { Icon: CloudHail, label: T.stormH[lang], tone: purple };
    default: return { Icon: Cloud, label: T.cloudy[lang], tone: slate };
  }
}

export function WeatherBadge({
  lat,
  lng,
  numDays = 1,
  lang,
}: {
  lat: number;
  lng: number;
  numDays?: number;
  lang: LangCode;
}) {
  const [days, setDays] = useState<DayForecast[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const fcDays = Math.min(Math.max(numDays, 1), 7);
    const url =
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
      `&daily=weather_code,temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,precipitation_sum,precipitation_probability_max,wind_speed_10m_max,uv_index_max` +
      `&timezone=auto&forecast_days=${fcDays}`;
    fetch(url)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const d = j?.daily;
        if (!d?.time?.length) {
          setError(true);
          return;
        }
        const out: DayForecast[] = d.time.map((date: string, i: number) => ({
          date,
          tMax: Math.round(d.temperature_2m_max[i]),
          tMin: Math.round(d.temperature_2m_min[i]),
          feelsMax: Math.round(d.apparent_temperature_max?.[i] ?? d.temperature_2m_max[i]),
          feelsMin: Math.round(d.apparent_temperature_min?.[i] ?? d.temperature_2m_min[i]),
          precipMm: d.precipitation_sum[i] ?? 0,
          precipProb: d.precipitation_probability_max[i] ?? 0,
          windMax: Math.round(d.wind_speed_10m_max?.[i] ?? 0),
          uvMax: Math.round(d.uv_index_max?.[i] ?? 0),
          code: d.weather_code[i] ?? 3,
        }));
        setDays(out);
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [lat, lng, numDays]);

  if (loading) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {L.weather[lang]}…
      </div>
    );
  }
  if (error || !days) return null;

  const fmtDay = (iso: string, idx: number) => {
    if (idx === 0) return L.today[lang];
    const d = new Date(iso);
    return d.toLocaleDateString(lang, { weekday: "short", day: "numeric" });
  };

  const first = days[0];
  const firstDesc = describeCode(first.code, lang);

  return (
    <div className="mt-4 rounded-xl border border-border bg-muted/20 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {L.weather[lang]}
        </span>
        <span className="text-[11px] font-medium text-muted-foreground">{firstDesc.label}</span>
      </div>

      {/* Today highlight: feels-like, wind, UV */}
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-background/60 px-2.5 py-1.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Thermometer className="h-3 w-3" /> {L.feels[lang]} {first.feelsMax}°
        </span>
        <span className="inline-flex items-center gap-1">
          <Wind className="h-3 w-3" /> {L.wind[lang]} {first.windMax} km/h
        </span>
        <span className="inline-flex items-center gap-1">
          <SunIcon className="h-3 w-3" /> {L.uv[lang]} {first.uvMax}
        </span>
      </div>

      <div className={`grid gap-2 ${days.length === 1 ? "grid-cols-1" : days.length <= 3 ? "grid-cols-3" : "grid-cols-4 sm:grid-cols-7"}`}>
        {days.map((d, i) => {
          const { Icon, label, tone } = describeCode(d.code, lang);
          return (
            <div
              key={d.date}
              className="flex flex-col items-center gap-0.5 rounded-lg bg-background/60 px-2 py-2 text-center"
              title={`${label} · ${d.precipProb}% ${L.rain[lang]} · ${L.feels[lang]} ${d.feelsMax}°`}
            >
              <span className="text-[10px] font-medium text-muted-foreground">{fmtDay(d.date, i)}</span>
              <Icon className={`h-5 w-5 ${tone}`} />
              <span className="text-xs font-semibold text-foreground">
                {d.tMax}° <span className="font-normal text-muted-foreground">/ {d.tMin}°</span>
              </span>
              {d.precipProb >= 30 && (
                <span className="text-[10px] text-sky-500">💧 {d.precipProb}%</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
